import React from 'react';
import { Helmet } from 'react-helmet-async';
import { alerts, news } from '../data/mockData';
import AlertBanner from '../components/AlertBanner';
import NewsCard from '../components/NewsCard';
import { FiAlertCircle, FiActivity, FiCheckCircle, FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const Home = () => {
  const urgentAlert = alerts.find(a => a.type === 'URGENT RECALL');

  return (
    <>
      <Helmet>
        <title>Recall Moringa | Safety News & Recall Database</title>
        <meta name="description" content="The leading source for moringa supplement safety reporting, recalls, and health alerts. Stay informed about outbreaks and product safety." />
      </Helmet>

      <AlertBanner alert={urgentAlert} />

      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200 py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            
            {/* Main Featured Story */}
            <div className="lg:w-2/3">
              <span className="text-primary font-bold tracking-wider text-sm uppercase mb-2 block">Top Story</span>
              <h1 className="text-3xl lg:text-5xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                CDC Investigates Multistate Salmonella Outbreak Linked to Moringa Capsules
              </h1>
              <div className="relative h-96 rounded-xl overflow-hidden mb-6 shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=2000&auto=format&fit=crop" 
                  alt="Moringa Powder Investigation" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                  <p className="text-white text-lg font-medium max-w-2xl">
                    Health officials warn consumers to avoid specific brands as investigation into extensively drug-resistant strain continues across 4 states.
                  </p>
                </div>
              </div>
              <Link to="/news/news-001" className="inline-block bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded-lg transition-colors">
                Read Full Investigation
              </Link>
            </div>

            {/* Sidebar: Recent Alerts */}
            <div className="lg:w-1/3 space-y-8">
              <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <FiAlertCircle className="text-alert" /> Safety Feed
                  </h2>
                  <span className="animate-pulse w-2 h-2 rounded-full bg-alert"></span>
                </div>
                <div className="space-y-4">
                  {alerts.map(alert => (
                    <div key={alert.id} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${alert.type === 'URGENT RECALL' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                        {alert.type}
                      </span>
                      <h3 className="font-semibold text-gray-900 mt-1 hover:text-primary cursor-pointer">
                        <Link to={`/alerts`}>{alert.title}</Link>
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{alert.source} • {alert.date}</p>
                    </div>
                  ))}
                </div>
                <Link to="/alerts" className="block text-center text-sm font-semibold text-primary mt-4 hover:underline">
                  View All Safety Alerts
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Current Status</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Active Recalls</span>
                    <span className="font-bold text-alert text-lg">3</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Outbreaks</span>
                    <span className="font-bold text-orange-600 text-lg">1</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Products Monitored</span>
                    <span className="font-bold text-primary text-lg">142</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-serif font-bold text-gray-900">Latest Reports</h2>
              <p className="text-gray-600 mt-2">Analysis, education, and industry updates.</p>
            </div>
            <Link to="/news" className="text-primary font-semibold hover:text-primary-dark flex items-center gap-1">
              View All News <FiChevronRight />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map(item => (
              <NewsCard key={item.id} news={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-primary text-white py-16">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FiCheckCircle className="text-5xl mx-auto mb-6 text-primary-light" />
          <h2 className="text-3xl font-bold mb-4">Verify Before You Buy</h2>
          <p className="text-lg text-blue-100 mb-8">
            Not all supplements are created equal. Check our comprehensive product database to see safety ratings, testing results, and recall history before purchasing moringa products.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="bg-white text-primary font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors">
              Search Database
            </Link>
            <Link to="/resources" className="bg-transparent border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white/10 transition-colors">
              Safety Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;